import React, { useState, useEffect } from 'react';
import { 
  Settings, Save, Plus, Edit2, Trash2, X, CheckCircle2, 
  DollarSign, Users, Building2, Phone, Eye, Lock, Unlock,
  LayoutDashboard, MessageSquare, Contact, Clock, Zap, Bot,
  TrendingUp, FileText, Send, Calendar, Award, BarChart3,
  UserCheck, CreditCard
} from 'lucide-react';
import axios from 'axios';
import api from '../services/api';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Ícones por ID da página
const pageIcons = {
  'dashboard': LayoutDashboard,
  'conversations': MessageSquare,
  'my-conversations': MessageSquare,
  'contacts': Contact,
  'tasks': Clock,
  'leads': Users,
  'team-performance': BarChart3,
  'appointments': Calendar,
  'bi-integration': BarChart3,
  'achievements': Award,
  'attendee-dashboard': LayoutDashboard,
  'users': Users,
  'sectors': Building2,
  'phone-numbers': Phone,
  'templates': FileText,
  'bulk-messaging': Send,
  'campaigns': BarChart3,
  'automations': Zap,
  'ai-agents': Bot,
  'opt-in': UserCheck,
  'reports': TrendingUp,
  'business-accounts': Building2,
  'profile': UserCheck,
  'funnel-board': TrendingUp,
  'chatbots': Bot,
};

function MasterSettings() {
  const [activeTab, setActiveTab] = useState('general');
  
  // ============================================
  // ABA 1: CONFIGURAÇÕES GERAIS
  // ============================================
  const [settings, setSettings] = useState({
    system_name: 'Soulidari',
    max_upload_size: '10',
    session_timeout: '480',
    enable_notifications: 'true',
    support_email: 'suporte@soulidari.com'
  });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  // ============================================
  // ABA 2: GERENCIAMENTO DE PLANOS
  // ============================================
  const [plans, setPlans] = useState([]);
  const [availablePages, setAvailablePages] = useState([]);
  const [groupedPages, setGroupedPages] = useState({});
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: '',
    slug: '',
    description: '',
    price: 0,
    max_users: 5,
    max_sectors: 5,
    whatsapp_numbers_limit: 3,
    allowed_pages: [],
    features: {},
    is_active: true,
    is_default: false,
    sort_order: 0
  });
  const [loadingPlans, setLoadingPlans] = useState(false);

  useEffect(() => {
    loadSettings();
    loadPlans();
    loadAvailablePages();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/master-admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({ ...settings, ...res.data });
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
    }
  };

  const loadPlans = async () => {
    setLoadingPlans(true);
    try {
      const res = await api.get('/api/plans?include_inactive=true');
      setPlans(res.data);
    } catch (err) {
      console.error('Erro ao carregar planos:', err);
    } finally {
      setLoadingPlans(false);
    }
  };

  const loadAvailablePages = async () => {
    try {
      const res = await api.get('/api/plans/available-pages');
      setAvailablePages(res.data.pages);
      setGroupedPages(res.data.grouped);
    } catch (err) {
      console.error('Erro ao carregar páginas:', err);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/master-admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Erro ao salvar configurações');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // PLANO: ABRIR MODAL (CRIAR/EDITAR)
  // ============================================
  const openPlanModal = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setPlanForm({
        name: plan.name,
        slug: plan.slug,
        description: plan.description || '',
        price: plan.price || 0,
        max_users: plan.max_users || 5,
        max_sectors: plan.max_sectors || 5,
        whatsapp_numbers_limit: plan.whatsapp_numbers_limit || 3,
        allowed_pages: plan.allowed_pages || [],
        features: plan.features || {},
        is_active: plan.is_active !== false,
        is_default: plan.is_default || false,
        sort_order: plan.sort_order || 0
      });
    } else {
      setEditingPlan(null);
      setPlanForm({
        name: '',
        slug: '',
        description: '',
        price: 0,
        max_users: 5,
        max_sectors: 5,
        whatsapp_numbers_limit: 3,
        allowed_pages: [],
        features: {},
        is_active: true,
        is_default: false,
        sort_order: plans.length + 1
      });
    }
    setShowPlanModal(true);
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const togglePage = (pageId) => {
    setPlanForm(prev => ({
      ...prev,
      allowed_pages: prev.allowed_pages.includes(pageId)
        ? prev.allowed_pages.filter(p => p !== pageId)
        : [...prev.allowed_pages, pageId]
    }));
  };

  const selectAllPages = () => {
    setPlanForm(prev => ({
      ...prev,
      allowed_pages: availablePages.map(p => p.id)
    }));
  };

  const clearAllPages = () => {
    setPlanForm(prev => ({ ...prev, allowed_pages: [] }));
  };

  const handleSavePlan = async () => {
    if (!planForm.name || !planForm.slug) {
      alert('Nome e slug são obrigatórios');
      return;
    }

    try {
      if (editingPlan) {
        await api.put(`/api/plans/${editingPlan.id}`, planForm);
        alert('✅ Plano atualizado com sucesso!');
      } else {
        await api.post('/api/plans', planForm);
        alert('✅ Plano criado com sucesso!');
      }
      setShowPlanModal(false);
      loadPlans();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeletePlan = async (plan) => {
    if (!window.confirm(`Excluir o plano "${plan.name}"?\n\nAtenção: Não é possível excluir planos que estão em uso.`)) return;

    try {
      await api.delete(`/api/plans/${plan.id}`);
      alert('✅ Plano excluído com sucesso!');
      loadPlans();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configurações do Sistema</h1>
        <p className="text-gray-500">Ajustes globais da plataforma</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 flex">
          <button
            onClick={() => setActiveTab('general')}
            className={`flex-1 px-6 py-4 font-medium text-sm transition-colors ${
              activeTab === 'general'
                ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50/30'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Settings size={16} className="inline mr-2" />
            Configurações Gerais
          </button>
          <button
            onClick={() => setActiveTab('plans')}
            className={`flex-1 px-6 py-4 font-medium text-sm transition-colors ${
              activeTab === 'plans'
                ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50/30'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <CreditCard size={16} className="inline mr-2" />
            Gerenciamento de Planos
            <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">
              {plans.length}
            </span>
          </button>
        </div>

        {/* ============================================ */}
        {/* ABA 1: CONFIGURAÇÕES GERAIS */}
        {/* ============================================ */}
        {activeTab === 'general' && (
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nome do Sistema</label>
              <input
                type="text"
                value={settings.system_name}
                onChange={e => setSettings({...settings, system_name: e.target.value})}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email de Suporte</label>
              <input
                type="email"
                value={settings.support_email}
                onChange={e => setSettings({...settings, support_email: e.target.value})}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tamanho Máximo de Upload (MB)</label>
                <input
                  type="number"
                  value={settings.max_upload_size}
                  onChange={e => setSettings({...settings, max_upload_size: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Timeout de Sessão (minutos)</label>
                <input
                  type="number"
                  value={settings.session_timeout}
                  onChange={e => setSettings({...settings, session_timeout: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notificações</label>
              <select
                value={settings.enable_notifications}
                onChange={e => setSettings({...settings, enable_notifications: e.target.value})}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
              >
                <option value="true">Ativadas</option>
                <option value="false">Desativadas</option>
              </select>
            </div>

            <div className="flex items-center justify-between pt-6 border-t border-gray-100">
              <button
                onClick={handleSaveSettings}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-xl font-medium hover:bg-orange-700 disabled:opacity-50">
                <Save size={20} />
                {loading ? 'Salvando...' : 'Salvar Configurações'}
              </button>
              {saved && (
                <span className="text-green-600 text-sm font-medium flex items-center gap-1">
                  <CheckCircle2 size={16} />
                  Configurações salvas com sucesso!
                </span>
              )}
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* ABA 2: GERENCIAMENTO DE PLANOS */}
        {/* ============================================ */}
        {activeTab === 'plans' && (
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Planos Disponíveis</h3>
                <p className="text-sm text-gray-500">Defina limites e permissões de cada plano</p>
              </div>
              <button
                onClick={() => openPlanModal()}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-xl font-medium hover:bg-orange-700 shadow-lg shadow-orange-100"
              >
                <Plus size={18} />
                Novo Plano
              </button>
            </div>

            {/* Grid de Planos */}
            {loadingPlans ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {plans.map(plan => (
                  <div 
                    key={plan.id} 
                    className={`bg-white border-2 rounded-2xl p-5 relative ${
                      !plan.is_active ? 'border-gray-200 opacity-60' : 'border-gray-100'
                    }`}
                  >
                    {plan.is_default && (
                      <div className="absolute -top-2 right-4 px-2 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full">
                        PADRÃO
                      </div>
                    )}
                    
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-bold text-gray-900">{plan.name}</h4>
                        <p className="text-xs text-gray-500 font-mono">{plan.slug}</p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openPlanModal(plan)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeletePlan(plan)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-3xl font-bold text-gray-900">
                        R$ {Number(plan.price).toFixed(2)}
                        <span className="text-xs font-normal text-gray-500">/mês</span>
                      </p>
                      {plan.description && (
                        <p className="text-xs text-gray-500 mt-1">{plan.description}</p>
                      )}
                    </div>

                    <div className="space-y-2 mb-4 pb-4 border-b border-gray-100">
                      <div className="flex items-center gap-2 text-sm">
                        <Users size={14} className="text-gray-400" />
                        <span className="text-gray-600">Usuários:</span>
                        <span className="font-bold text-gray-900">{plan.max_users}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 size={14} className="text-gray-400" />
                        <span className="text-gray-600">Setores:</span>
                        <span className="font-bold text-gray-900">{plan.max_sectors}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Phone size={14} className="text-gray-400" />
                        <span className="text-gray-600">WhatsApp:</span>
                        <span className="font-bold text-gray-900">{plan.whatsapp_numbers_limit}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Eye size={14} className="text-gray-400" />
                        <span className="text-gray-600">Páginas:</span>
                        <span className="font-bold text-gray-900">{plan.allowed_pages?.length || 0}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        {plan.is_active ? (
                          <>
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-xs text-green-700 font-medium">Ativo</span>
                          </>
                        ) : (
                          <>
                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                            <span className="text-xs text-gray-500 font-medium">Inativo</span>
                          </>
                        )}
                      </div>
                      <button
                        onClick={() => openPlanModal(plan)}
                        className="text-xs text-orange-600 font-medium hover:text-orange-700"
                      >
                        Editar →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* MODAL DE PLANO */}
      {/* ============================================ */}
      {showPlanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <CreditCard size={24} />
                  {editingPlan ? 'Editar Plano' : 'Criar Novo Plano'}
                </h2>
                <p className="text-sm opacity-90 mt-1">
                  Configure os limites e permissões
                </p>
              </div>
              <button
                onClick={() => setShowPlanModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Informações Básicas */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <Settings size={16} />
                  Informações Básicas
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Nome do Plano *</label>
                    <input
                      type="text"
                      value={planForm.name}
                      onChange={e => {
                        const name = e.target.value;
                        setPlanForm({
                          ...planForm, 
                          name,
                          slug: generateSlug(name)
                        });
                      }}
                      placeholder="Ex: Premium Gold"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Slug (URL) *</label>
                    <input
                      type="text"
                      value={planForm.slug}
                      onChange={e => setPlanForm({...planForm, slug: e.target.value})}
                      placeholder="premium-gold"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none font-mono"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Descrição</label>
                    <input
                      type="text"
                      value={planForm.description}
                      onChange={e => setPlanForm({...planForm, description: e.target.value})}
                      placeholder="Descrição curta do plano"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      <DollarSign size={12} className="inline" /> Preço Mensal (R$)
                    </label>
                    <input
                      type="text"
                      value={planForm.price === 0 ? '' : planForm.price}
                      onChange={e => {
                        // Remove pontos (separador de milhar) e substitui vírgula por ponto
                        let raw = e.target.value.replace(/\./g, '');
                        raw = raw.replace(',', '.');
                        const value = parseFloat(raw) || 0;
                        setPlanForm({...planForm, price: value});
                      }}
                      placeholder="Ex: 1260 ou 1.260"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Ordem de Exibição</label>
                    <input
                      type="number"
                      value={planForm.sort_order}
                      onChange={e => setPlanForm({...planForm, sort_order: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                    />
                  </div>
                  <div className="col-span-2 flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={planForm.is_active}
                        onChange={e => setPlanForm({...planForm, is_active: e.target.checked})}
                        className="w-4 h-4 text-orange-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Plano Ativo</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={planForm.is_default}
                        onChange={e => setPlanForm({...planForm, is_default: e.target.checked})}
                        className="w-4 h-4 text-orange-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Plano Padrão (novas empresas)</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Limites */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <h3 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
                  <Lock size={16} />
                  Limites do Plano
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-blue-800 mb-1">
                      <Users size={12} className="inline" /> Máx. Usuários
                    </label>
                    <input
                      type="number"
                      value={planForm.max_users}
                      onChange={e => setPlanForm({...planForm, max_users: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-blue-800 mb-1">
                      <Building2 size={12} className="inline" /> Máx. Setores
                    </label>
                    <input
                      type="number"
                      value={planForm.max_sectors}
                      onChange={e => setPlanForm({...planForm, max_sectors: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-blue-800 mb-1">
                      <Phone size={12} className="inline" /> Números WhatsApp
                    </label>
                    <input
                      type="number"
                      value={planForm.whatsapp_numbers_limit}
                      onChange={e => setPlanForm({...planForm, whatsapp_numbers_limit: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Permissões de Páginas */}
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-purple-900 flex items-center gap-2">
                    <Eye size={16} />
                    Páginas Permitidas ({planForm.allowed_pages.length}/{availablePages.length})
                  </h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={selectAllPages}
                      className="text-xs px-3 py-1 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      Selecionar Todas
                    </button>
                    <button
                      type="button"
                      onClick={clearAllPages}
                      className="text-xs px-3 py-1 bg-white border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-100"
                    >
                      Limpar
                    </button>
                  </div>
                </div>

                <p className="text-xs text-purple-700 mb-3">
                  Selecione quais páginas os usuários da empresa poderão ver no menu lateral.
                </p>

                <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                  {Object.entries(groupedPages).map(([group, pages]) => (
                    <div key={group} className="bg-white rounded-lg p-3 border border-purple-100">
                      <h4 className="text-xs font-bold text-gray-700 uppercase mb-2">{group}</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {pages.map(page => {
                          const Icon = pageIcons[page.id] || Settings;
                          const isSelected = planForm.allowed_pages.includes(page.id);
                          return (
                            <label
                              key={page.id}
                              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                                isSelected 
                                  ? 'bg-purple-100 border border-purple-300' 
                                  : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => togglePage(page.id)}
                                className="w-4 h-4 text-purple-600 rounded"
                              />
                              <Icon size={14} className={isSelected ? 'text-purple-600' : 'text-gray-400'} />
                              <span className={`text-xs font-medium ${isSelected ? 'text-purple-900' : 'text-gray-700'}`}>
                                {page.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ações */}
              <div className="flex gap-3 justify-end pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowPlanModal(false)}
                  className="px-6 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSavePlan}
                  className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-bold hover:from-orange-600 hover:to-orange-700 shadow-lg shadow-orange-200"
                >
                  {editingPlan ? 'Salvar Alterações' : 'Criar Plano'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MasterSettings;
